<script>
export default {
  name: "DidoDatafile",
};
</script>

<script setup>
import { ref, watch, computed, inject } from "vue";
import api from "../api";
import {
  displayDateFormatDDMMYYYY,
  displayWithBR,
  cleanLinkedDocTitle,
  pause,
} from "../utils";
import DidoTextTruncate from "./commons/Truncate.vue";
import DidoMillesime from "./Millesime.vue";
import DidoChip from "./commons/chips/Chip.vue";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datafileRid: { type: String, required: false, default: "" },
  datafileMillesime: { type: String, required: false, default: "" },
  datafile: { type: Object, required: false, default: null },
  millesimeSelectedTab: { type: String, required: false, default: "datas" },
  linkedDocsApiUrl: { type: String, required: false, default: null },
  initialFilter: { type: String, required: false, default: null },
});

// define events emitted
const emit = defineEmits([
  "datafile-retrieved",
  "datafile-not-retrieved",
  "label-selection",
  "dataset-selection",
  "millesime-selection",
  "tab-changed-in-millesime",
  "bad-configuration",
]);

// define Reactive Data
const status = ref("");
const loading = ref(false);
const datafileObject = ref(null);
const linkedDocs = ref([]);
const selectedMillesime = ref(null);
const selectedMillesimeObject = ref(null);
const millesimeSelectedTab = ref(props.millesimeSelectedTab);

// define watchers
watch(
  () => props.datafileMillesime,
  () => {
    selectedMillesime.value = null;
    selectedMillesimeObject.value = null;
    getMillesime();
  },
);

watch(
  () => [props.apiUrl, props.datafileRid, props.datafile],
  () => {
    status.value = "";
    loading.value = false;
    datafileObject.value = null;
    linkedDocs.value = [];
    selectedMillesime.value = null;
    selectedMillesimeObject.value = null;
    getMillesime();
  },
);

watch(
  () => [props.millesimeSelectedTab],
  () => {
    millesimeSelectedTab.value = props.millesimeSelectedTab;
  },
);

// define computed datas
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.datafileRid && !props.datafile) return false;
  return true;
});
const modificationDate = computed(() => {
  const dateToDisplay =
    datafileObject.value.last_modified || datafileObject.value.created_at;
  return displayDateFormatDDMMYYYY(dateToDisplay);
});
const publicationDate = computed(() => {
  if (datafileObject.value.published)
    return displayDateFormatDDMMYYYY(datafileObject.value.published);
  return "";
});
const temporalCoverageStart = computed(() => {
  if (datafileObject.value.temporal_coverage)
    return displayDateFormatDDMMYYYY(
      datafileObject.value.temporal_coverage.start,
    );
  return "";
});
const temporalCoverageEnd = computed(() => {
  if (datafileObject.value.temporal_coverage)
    return displayDateFormatDDMMYYYY(
      datafileObject.value.temporal_coverage.end,
    );
  return "";
});
const millesimesOptions = computed(() => {
  if (datafileObject.value.millesimes)
    return datafileObject.value.millesimes
      .map((m) => m.millesime)
      .sort()
      .map((m) => ({ label: m, value: m }))
      .reverse();
  return [];
});

// inject variables for interaction with catalog
const catalogUrl = inject("catalogUrl", () => null);
const inCatalog = inject("inCatalog", null);

// Define methods
const getDatafile = async () => {
  // if widget bad configured
  if (!configured.value) {
    status.value = "bad-configured";
    loading.value = false;
    datafileObject.value = null;
    emit("bad-configuration");
    return;
  }

  loading.value = true;
  // if datafile data is available , setting it in reactive datas (no api call required !!)
  if (props.datafile) {
    datafileObject.value = props.datafile;
    status.value = "available";
    loading.value = false;
  }
  // if dataset id is set, api call for retrieving dataset and setting it in reactive datas
  if (props.datafileRid) {
    await Promise.all([
      api.getDatafile(props.apiUrl, props.datafileRid),
      pause(500),
    ])
      .then(([apiResult]) => {
        datafileObject.value = apiResult.datafile;
        status.value = apiResult.status;
        if (status.value !== "available")
          emit("datafile-not-retrieved", {
            datafileRid: props.datafileRid,
          });
      })
      .finally(() => (loading.value = false));
    if (!datafileObject.value) return;

    emit("datafile-retrieved", { datafileRid: props.datafileRid });
  }
};
const getMillesime = async () => {
  // retrieve datafile
  if (!datafileObject.value) await getDatafile();

  if (!datafileObject.value) return;

  // get linkedDocs
  if (props.linkedDocsApiUrl) {
    api
      .getDatafileLinkedDocs(props.linkedDocsApiUrl, datafileObject.value.rid)
      .then((docs) => (linkedDocs.value = docs));
  }

  // if didoDatafileMillesime is not null, check if millesime exists in datafile
  if (props.datafileMillesime) {
    const checkMillesime = datafileObject.value.millesimes.find(
      ({ millesime }) => millesime === props.datafileMillesime,
    );
    // checkMillesime is false => get last millesime as selected millesime
    if (!checkMillesime) {
      selectedMillesime.value = datafileObject.value.millesimes
        .map(({ millesime }) => millesime)
        .sort()
        .reverse()[0];
      selectedMillesimeObject.value = {
        ...datafileObject.value.millesimes.find(
          ({ millesime }) => millesime === selectedMillesime.value,
        ),
        rid: datafileObject.value.rid,
        dataset: datafileObject.value.dataset,
      };
      return;
    }
    // checkMillesime is true => set this millesime as selected millesime
    selectedMillesime.value = props.datafileMillesime;
    selectedMillesimeObject.value = {
      ...datafileObject.value.millesimes.find(
        ({ millesime }) => millesime === selectedMillesime.value,
      ),
      rid: datafileObject.value.rid,
      dataset: datafileObject.value.dataset,
    };
  }
  // didoDatafileMillesime is null => get last millesime as selected millesime
  else {
    selectedMillesime.value = datafileObject.value.millesimes
      .map(({ millesime }) => millesime)
      .sort()
      .reverse()[0];
    selectedMillesimeObject.value = {
      ...datafileObject.value.millesimes.find(
        ({ millesime }) => millesime === selectedMillesime.value,
      ),
      rid: datafileObject.value.rid,
      dataset: datafileObject.value.dataset,
    };
    return;
  }
};

// Init
getMillesime();
</script>

<template>
  <div class="dido-widget-datafile relative-position">
    <!-- Loading is finished -->
    <template v-if="!loading">
      <!-- Bad configuration of widget -->
      <div
        v-if="status === 'bad-configured'"
        class="q-pa-sm row-start-center border-secondary text-black"
      >
        <div class="col-12">Impossible de charger le composant...</div>
        <div class="col-12">Les attributs suivant sont requis:</div>
        <ul class="col-12">
          <li>api-url</li>
          <li>datafile-rid ou datafile</li>
        </ul>
      </div>

      <!-- Datafile unavailable-->
      <div
        v-if="!['available', 'bad-configured'].includes(status)"
        class="q-pa-sm column-all-center border-secondary text-black"
      >
        <div class="row-all-center">
          <QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-0-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          />
        </div>
        <div class="row-all-center q-mt-md">
          Impossible d'afficher le fichier de données
          <QIcon
            name="mdi-sync"
            size="xs"
            class="on-right cursor-pointer"
            color="secondary"
            title="Réessayer de charger les données"
            @click="getDatafile"
          />
        </div>
      </div>

      <!-- Datafile is here -->
      <div
        v-if="status === 'available'"
        class="dido-main-object row q-col-gutter-none"
      >
        <!-- Metadata -->
        <div class="bg-grey-4 col-12 row border-box q-pa-md">
          <!-- Title, Timestamps and Description -->
          <div
            class="full-height col-6 col-md-7 row content-start q-pr-md bordered-black-right"
          >
            <!-- Title -->
            <div class="col-12">
              <span class="dido-object-title">{{ datafileObject.title }}</span>
            </div>
            <!-- Timestamps -->
            <div
              :class="`col-12 q-my-sm row q-col-gutter-${
                $q.screen.gt.sm ? 'sm' : 'xs'
              } dido-object-timestamps`"
            >
              <!-- Modification Date -->
              <span class="col-12 col-sm-auto">
                Modifié le {{ modificationDate }}
              </span>
              <!-- Separator -->
              <span v-if="$q.screen.gt.xs" class="col-0 col-sm-auto">|</span>
              <!-- Publication Date -->
              <span class="col-12 col-sm-auto">
                Publié le {{ publicationDate }}
              </span>
            </div>
            <!-- Dataset information -->
            <div class="col-12 q-mt-xs q-mb-md text-small">
              <span>Ce fichier appartient au jeu de données</span>
              <a
                v-if="inCatalog"
                class="q-mx-sm text-primary cursor-pointer dido-datafile-dataset-resume-link"
                :href="
                  catalogUrl({
                    type: 'dataset',
                    datasetId: datafileObject.dataset.id,
                  })
                "
                :title="`Voir le détail du jeu de données ${datafileObject.dataset.title}`"
                @click.prevent="
                  emit('dataset-selection', {
                    datasetId: datafileObject.dataset.id,
                  })
                "
              >
                {{ datafileObject.dataset.title }}
              </a>
              <span v-else class="q-mx-sm text-info cursor-inherit">
                {{ datafileObject.dataset.title }}
              </span>
            </div>
            <!-- Description -->
            <div class="col-12">
              <DidoTextTruncate
                :text="datafileObject.description"
                :length="200"
                class="text-justify"
                :link-classes="[
                  'cursor-pointer',
                  'on-right',
                  'text-primary',
                  'text-underline',
                  'text-small',
                ]"
              />
            </div>
          </div>

          <!-- Other Metadatas -->
          <div
            class="full-height col-6 col-md-5 row content-start q-pl-md q-col-gutter-x-none q-col-gutter-y-md"
          >
            <!-- Topic -->
            <div class="col-12 col-sm-4">
              <div
                :class="`text-left text-bold ${
                  $q.screen.gt.xs ? 'bordered-black-right' : ''
                }`"
              >
                THÈME
              </div>
              <div class="text-left">
                <DidoChip
                  type="topic"
                  from-dataset-or-datafile="datafile"
                  :item="{
                    id: datafileObject.dataset.topic,
                    display: datafileObject.dataset.topic,
                  }"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- License -->
            <div :class="`col-12 col-sm-4 ${$q.screen.gt.xs ? 'q-pl-sm' : ''}`">
              <div class="text-left text-bold">LICENCE</div>
              <div class="text-left">
                <DidoChip
                  type="license"
                  from-dataset-or-datafile="datafile"
                  :item="{
                    id: datafileObject.dataset.license,
                    display: datafileObject.dataset.license,
                  }"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- Temporal coverage -->
            <div v-if="datafileObject.temporal_coverage" class="col-12">
              <div class="text-left text-bold">COUVERTURE TEMPORELLE</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <span class="text-small">
                  {{ temporalCoverageStart }} au {{ temporalCoverageEnd }}
                </span>
              </div>
            </div>
            <!-- Legal Notice -->
            <div v-if="datafileObject.legal_notice" class="col-12">
              <div class="text-left text-bold">NOTICE LÉGALE</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <div
                  class="text-justify text-small"
                  v-html="displayWithBR(datafileObject.legal_notice)"
                />
              </div>
            </div>
            <!-- Downloads Counter -->
            <div v-if="datafileObject.downloads" class="col-12">
              <div class="text-left text-bold">NOMBRE DE TÉLÉCHARGEMENTS</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <span class="text-small">
                  {{ datafileObject.downloads.toLocaleString() }}
                </span>
              </div>
            </div>
            <!-- Linked Docs -->
            <div v-if="linkedDocs.length" class="col-12">
              <div class="text-left text-bold">PUBLICATIONS ASSOCIÉES</div>
              <div
                v-for="(linkedDoc, i) of linkedDocs"
                :key="`linkedDoc-${i}`"
                class="text-left"
              >
                <QBtn
                  :href="linkedDoc.url"
                  target="_blank"
                  icon-right="mdi-open-in-new"
                  size="sm"
                  flat
                  dense
                  class="text-small"
                  no-caps
                  no-wrap
                  color="secondary"
                  :title="`Ouvrir la publication '${cleanLinkedDocTitle(
                    linkedDoc.title,
                  )}' dans une nouvelle fenêtre`"
                  :label="cleanLinkedDocTitle(linkedDoc.title)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Millesimes Choice -->
        <div
          v-if="millesimesOptions.length > 1"
          class="q-mt-md col-12 text-small"
        >
          Le fichier de données existe dans
          {{ millesimesOptions.length }} millésimes :
          <QBtnToggle
            v-model="selectedMillesime"
            class="dido-datafile-millesimeschoice-button"
            size="sm"
            flat
            color="black"
            toggle-color="info"
            :options="millesimesOptions"
            @update:model-value="
              emit('millesime-selection', {
                datafileRid: datafileObject.rid,
                datafileMillesime: selectedMillesime,
              })
            "
          />
        </div>

        <!-- Millesime Selected -->
        <div class="q-mt-md col-12">
          <!-- Millesime widget WebComponent -->
          <DidoMillesime
            v-if="datafileObject && selectedMillesimeObject"
            :key="`${datafileObject.rid}-${selectedMillesimeObject.millesime}`"
            :api-url="props.apiUrl"
            :datafile-rid="datafileObject.rid"
            :millesime-object="selectedMillesimeObject"
            :initial-filter="props.initialFilter"
            :selected-tab="millesimeSelectedTab"
            @tab-changed-in-millesime="
              (newTab) =>
                emit('tab-changed-in-millesime', {
                  tab: newTab,
                  datafileRid,
                  datafileRid: datafileObject.rid,
                  datafileMillesime: selectedMillesime,
                })
            "
          />
        </div>
      </div>
    </template>

    <!-- Spinner during interaction with api -->
    <QInnerLoading :showing="loading">
      <QSpinner size="50px" color="secondary" />
      <div class="text-h6 text-secondary">
        Chargement du fichier de données en cours
      </div>
    </QInnerLoading>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-datafile
  min-height: 200px
  .dido-object-title
    word-break: break-word
  .dido-datafile-dataset-resume-link
    border-bottom: 1px dotted currentColor
    text-decoration: none
  .dido-datafile-millesimeschoice-button
    :deep( button.text-info)
      cursor: inherit
      span.q-btn__content
        border-bottom: 1px dotted currentColor
        text-decoration: none
</style>
